/**
 * Service Actions API (New Backend)
 * Per `DASHBOARD_DOCUMENTATION.md` all mutations are REST.
 * 
 * Service FSM States: pending → accepted → scheduled → in_progress → done → completed
 * Alternative paths: cancelled, on_hold
 */

import apiClient from '@/api/client'

export interface ServiceActionResult<T = unknown> {
  success: boolean
  message?: string
  service?: T
}

export interface ScheduleServiceInput {
  scheduled_start: string
  scheduled_end?: string
  provider_id?: string
}

export interface AssignProviderInput {
  provider_profile_id: string
}

// ============================================================================
// Service FSM Actions
// ============================================================================

/**
 * Accept a service (pending → accepted)
 */
export async function acceptService(serviceId: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(`/api/v1/orders/services/${serviceId}/accept/`)
  return resp.data
}

/**
 * Decline a service (pending → declined/cancelled)
 */
export async function declineService(serviceId: string, reason?: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/decline/`,
    reason ? { reason } : undefined
  )
  return resp.data
}

/**
 * Schedule a service (accepted → scheduled)
 */
export async function scheduleService(
  serviceId: string,
  input: ScheduleServiceInput
): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/schedule/`,
    input
  )
  return resp.data
}

/**
 * Start a service (scheduled → in_progress)
 */
export async function startService(serviceId: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(`/api/v1/orders/services/${serviceId}/start/`)
  return resp.data
}

/**
 * Mark service as done by provider (in_progress → done)
 */
export async function markServiceDone(serviceId: string, notes?: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/mark-done/`,
    notes ? { notes } : undefined
  )
  return resp.data
}

/**
 * Complete a service - final confirmation (done → completed)
 */
export async function completeService(serviceId: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(`/api/v1/orders/services/${serviceId}/complete/`)
  return resp.data
}

/**
 * Cancel a service
 */
export async function cancelService(serviceId: string, reason?: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/cancel/`,
    reason ? { reason } : undefined
  )
  return resp.data
}

/**
 * Put service on hold
 */
export async function holdService(serviceId: string, reason?: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/hold/`,
    reason ? { reason } : undefined
  )
  return resp.data
}

/**
 * Resume a service from hold
 */
export async function resumeService(serviceId: string): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(`/api/v1/orders/services/${serviceId}/resume/`)
  return resp.data
}

/**
 * Assign a provider to a service
 */
export async function assignProvider(
  serviceId: string,
  input: AssignProviderInput
): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/assign-provider/`,
    input
  )
  return resp.data
}

/**
 * Entity dispatch: assign a provider AND accept the service on their behalf in
 * one atomic call (assigned → accepted → scheduled). No separate accept call needed.
 * POST /api/v1/orders/services/{id}/entity-assign-provider/
 *
 * Returns structured errors on failure ({ error, code }) — e.g.
 * SERVICE_NOT_IN_ENTITY_SCOPE, PROVIDER_NOT_IN_ENTITY, INVALID_SERVICE_STATUS,
 * ENTITY_ASSIGNMENT_NOT_ALLOWED. axios rejects on non-2xx, so a resolved call = success.
 */
export async function entityAssignProvider(
  serviceId: string,
  input: AssignProviderInput
): Promise<ServiceActionResult> {
  const resp = await apiClient.post<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/entity-assign-provider/`,
    input
  )
  return resp.data
}

/**
 * Get service workflow status
 */
export async function getServiceWorkflow(serviceId: string): Promise<ServiceActionResult> {
  const resp = await apiClient.get<ServiceActionResult>(
    `/api/v1/orders/services/${serviceId}/workflow/`
  )
  return resp.data
}

